import { useCallback, useEffect } from 'react';
import { useLayerStore } from '../store/layer.store';
import { useMapStore } from '../store/map.store';
import { Layer } from '../types/api';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import { geoserverEndpoint, geoserverLegendUrl } from '@/utils/geoserverAccess';

/* ══════════════════════════════════════════════════════════════
   Z-Index hierarchy (matching legacy layer.manager.js)
   ══════════════════════════════════════════════════════════════
   Base maps:       0   (handled in MapView.tsx)
   Thematics:       1
   Administrative:  1
   User Overlays:  73
   POI Overlays:   73
   Labels:        100
   Config/Drawing: 200+ (handled in useMapInteractions)
   ══════════════════════════════════════════════════════════════ */
const Z_INDEX = {
  thematic: 1,
  administrative: 1,
  user: 73,
  poi: 73,
  label: 100,
  conf: 200,
} as const;

/** Resolve z-index for a layer based on its menu category */
function getZIndexForLayer(layer: Layer): number {
  const menuName = (layer.menu?.name || '').toLowerCase();
  if (menuName.includes('thematic')) return Z_INDEX.thematic;
  if (menuName.includes('administrative') || menuName.includes('border')) return Z_INDEX.administrative;
  if (menuName.includes('poi')) return Z_INDEX.poi;
  if (menuName.includes('label')) return Z_INDEX.label;
  if (menuName.includes('conf') || menuName.includes('drawing')) return Z_INDEX.conf;
  return Z_INDEX.user; // default: user overlays
}

/** Check if a layer is a thematic layer */
function isThematicLayer(layer: Layer): boolean {
  return (layer.menu?.name || '').toLowerCase().includes('thematic');
}

/** WMS URL for a layer, through the backend proxy (login required). */
function getWmsUrl(layer: Layer): string {
  return geoserverEndpoint(layer.layerWorkspace?.name || 'cite', 'wms');
}

/** Construct the WMS LAYERS param: workspace:layerName */
function getWmsLayerParam(layer: Layer): string {
  const ws = layer.layerWorkspace?.name || 'cite';
  return `${ws}:${layer.layerName}`;
}

/** Layer names/keywords that should be visible by default */
const DEFAULT_VISIBLE_PATTERNS = [
  'neighbourhood_ses', 'neighborhood_ses',
  'geomahalle', 'mahalle_ses',
];

function matchesDefaultVisible(layer: Layer): boolean {
  const name = (layer.layerName || '').toLowerCase();
  const display = (layer.displayName || '').toLowerCase();
  return DEFAULT_VISIBLE_PATTERNS.some(
    (pat) => name.includes(pat) || display.includes(pat),
  );
}

/* ── Module-level flags to prevent duplicate init across hook instances ── */
let _layersLoadStarted = false;
let _defaultLayersApplied = false;
/** Track which map instance we last applied layers to */
let _lastMapUid: string | null = null;

/** Reset layer flags so the next mount re-applies layers to a fresh map */
export function resetLayerFlags() {
  _defaultLayersApplied = false;
  _lastMapUid = null;
}

/**
 * Forget everything about the signed-in person's layers: on sign-out, so the next person (another company)
 * gets their own list instead of the previous one's, without a page reload.
 */
export function resetLayerSession() {
  _layersLoadStarted = false;
  resetLayerFlags();
  useLayerStore.setState({
    allLayers: [], borders: [], thematics: [], userOverlays: [], pois: [], labelLayers: [], confLayers: [],
    thematicGroups: [], poiGroups: [], visibility: {}, labelVisibility: {}, error: null,
  });
}

export const useLayers = () => {
  const {
    allLayers,
    borders,
    thematics,
    userOverlays,
    pois,
    labelLayers,
    confLayers,
    thematicGroups,
    poiGroups,
    visibility,
    labelVisibility,
    favorites,
    isLoading,
    error,
    loadAllLayers,
    loadLabelLayers,
    toggleVisibility,
    setLayerVisible,
    toggleLabelVisibility,
    toggleFavorite,
    clearError,
  } = useLayerStore();

  const { mapInstance: map } = useMapStore();

  /** Create and add an OpenLayers WMS layer to the map.
   *  Uses ImageWMS (single request per view) instead of TileWMS to avoid
   *  overwhelming GeoServer with hundreds of tile requests (429 Too Many Requests).
   */
  const addOlLayer = useCallback(
    (mapInst: any, layer: Layer, forceVisible = true) => {
      const zIndex = getZIndexForLayer(layer);

      const olLayer = new ImageLayer({
        source: new ImageWMS({
          url: getWmsUrl(layer),
          params: { LAYERS: getWmsLayerParam(layer) },
          serverType: 'geoserver',
          crossOrigin: 'anonymous',
          ratio: 1, // request exactly the viewport extent, no extra buffer
        }),
        properties: { name: layer.displayName || layer.layerName, layerName: layer.layerName, layerId: layer.id },
        visible: forceVisible,
        zIndex,
      });
      mapInst.addLayer(olLayer);
      return olLayer;
    },
    [],
  );

  // Load layers on mount (once across ALL hook instances)
  useEffect(() => {
    if (!_layersLoadStarted) {
      _layersLoadStarted = true;
      loadAllLayers();
      loadLabelLayers();
    }
  }, [loadAllLayers, loadLabelLayers]);

  // After layers loaded + map ready, enable default-visible layers (NO labels)
  // Uses module-level flag so only first hook instance triggers this
  useEffect(() => {
    if (_defaultLayersApplied || !map || allLayers.length === 0) return;
    _defaultLayersApplied = true;

    for (const layer of allLayers) {
      if (!matchesDefaultVisible(layer)) continue;
      // Skip label layers — never open by default
      const menuName = (layer.menu?.name || '').toLowerCase();
      if (menuName.includes('label')) continue;

      // Also skip if a layer with same layerId is already on the map
      let alreadyAdded = false;
      map.getLayers().forEach((l: any) => {
        if (l.get('layerId') === layer.id) alreadyAdded = true;
      });
      if (alreadyAdded) continue;

      setLayerVisible(layer.id, true);
      addOlLayer(map, layer);
    }
  }, [map, allLayers, setLayerVisible]);

  // ── Re-add visible layers when coming back to a NEW map instance ──
  // When the user navigates away (e.g. admin) and back, MapView creates a
  // fresh OL map.  The Zustand layer store still remembers which layers are
  // visible, but the new map has no OL layer objects.  Detect the new map
  // by its unique OL uid and re-add every layer the store says is visible.
  useEffect(() => {
    if (!map || allLayers.length === 0) return;

    const uid = (map as any).ol_uid ?? (map as any).getProperties?.()?.ol_uid ?? String(map);
    if (_lastMapUid === uid) return;          // same map, nothing to do
    const isFirstMount = _lastMapUid === null;
    _lastMapUid = uid;
    if (isFirstMount) return;                 // first ever mount handled by default-layers effect above

    // This is a RE-mount: restore all layers the store says are visible
    const storeVisibility = useLayerStore.getState().visibility;
    const storeLabelVisibility = useLayerStore.getState().labelVisibility;

    for (const layer of allLayers) {
      if (storeVisibility[layer.id]) {
        // Check not already on new map
        let alreadyAdded = false;
        map.getLayers().forEach((l: any) => {
          if (l.get('layerId') === layer.id) alreadyAdded = true;
        });
        if (!alreadyAdded) {
          addOlLayer(map, layer, true);
        }
      }
    }

    // Also restore visible label layers
    const storeLabelLayers = useLayerStore.getState().labelLayers;
    for (const lbl of storeLabelLayers) {
      if (storeLabelVisibility[lbl.id]) {
        let alreadyAdded = false;
        map.getLayers().forEach((l: any) => {
          if (l.get('layerId') === lbl.id) alreadyAdded = true;
        });
        if (!alreadyAdded) {
          addOlLayer(map, lbl, true);
        }
      }
    }
  }, [map, allLayers, addOlLayer]);

  /** Find OL layer by layerId */
  const findOlLayer = useCallback(
    (layerId: number): any => {
      if (!map) return null;
      let found: any = null;
      map.getLayers().forEach((l: any) => {
        if (l.get('layerId') === layerId) found = l;
      });
      return found;
    },
    [map],
  );

  /** Toggle WMS layer visibility on the OpenLayers map */
  const handleToggleVisibility = useCallback(
    (layerId: number) => {
      const layer = allLayers.find((l) => l.id === layerId);
      if (!layer || !map) return;

      const newVisible = !visibility[layerId];

      // ─── Thematic exclusivity: close any other visible thematic ───
      if (newVisible && isThematicLayer(layer)) {
        for (const tLayer of thematics) {
          if (tLayer.id !== layerId && visibility[tLayer.id]) {
            // Turn off the currently visible thematic
            setLayerVisible(tLayer.id, false);
            const olT = findOlLayer(tLayer.id);
            if (olT) olT.setVisible(false);
          }
        }
      }

      // Toggle this layer
      toggleVisibility(layerId);

      const olLayer = findOlLayer(layerId);
      if (olLayer) {
        olLayer.setVisible(newVisible);
      } else if (newVisible) {
        addOlLayer(map, layer, true);
      }
    },
    [map, allLayers, thematics, visibility, toggleVisibility, setLayerVisible, findOlLayer, addOlLayer],
  );

  /** Toggle label layer visibility */
  const handleToggleLabelVisibility = useCallback(
    (dataLayerId: number) => {
      if (!map) return;

      const dataLayer = allLayers.find((l) => l.id === dataLayerId);
      if (!dataLayer) return;

      const dataName = (dataLayer.displayName || dataLayer.layerName || '').toLowerCase();
      let matchedLabel: Layer | undefined;

      for (const lbl of labelLayers) {
        const lblName = (lbl.displayName || lbl.layerName || '').toLowerCase();
        if (
          (dataName.includes('province') && lblName.includes('province')) ||
          (dataName.includes('district') && lblName.includes('district')) ||
          (dataName.includes('neighbor') && lblName.includes('neighbor')) ||
          (dataName.includes('mahalle') && lblName.includes('mahalle')) ||
          lblName.includes(dataName)
        ) {
          matchedLabel = lbl;
          break;
        }
      }

      if (!matchedLabel) {
        for (const lbl of labelLayers) {
          const lblName = (lbl.displayName || lbl.layerName || '').toLowerCase();
          if (dataName.includes(lblName.replace('label', '').trim())) {
            matchedLabel = lbl;
            break;
          }
        }
      }

      if (!matchedLabel) return;

      toggleLabelVisibility(matchedLabel.id);
      const newVisible = !labelVisibility[matchedLabel.id];

      let olLayer = findOlLayer(matchedLabel.id);
      if (olLayer) {
        olLayer.setVisible(newVisible);
      } else if (newVisible) {
        addOlLayer(map, matchedLabel, true);
      }
    },
    [map, allLayers, labelLayers, labelVisibility, toggleLabelVisibility, findOlLayer, addOlLayer],
  );

  /** Check if a data layer has a matching label layer */
  const hasLabel = useCallback(
    (dataLayerId: number): boolean => {
      const dataLayer = allLayers.find((l) => l.id === dataLayerId);
      if (!dataLayer) return false;
      const dataName = (dataLayer.displayName || dataLayer.layerName || '').toLowerCase();
      return labelLayers.some((lbl) => {
        const lblName = (lbl.displayName || lbl.layerName || '').toLowerCase();
        return (
          (dataName.includes('province') && lblName.includes('province')) ||
          (dataName.includes('district') && lblName.includes('district')) ||
          (dataName.includes('neighbor') && lblName.includes('neighbor')) ||
          (dataName.includes('mahalle') && lblName.includes('mahalle')) ||
          lblName.includes(dataName) ||
          dataName.includes(lblName.replace('label', '').trim())
        );
      });
    },
    [allLayers, labelLayers],
  );

  const isLabelVisible = useCallback(
    (dataLayerId: number): boolean => {
      const dataLayer = allLayers.find((l) => l.id === dataLayerId);
      if (!dataLayer) return false;
      const dataName = (dataLayer.displayName || dataLayer.layerName || '').toLowerCase();
      for (const lbl of labelLayers) {
        const lblName = (lbl.displayName || lbl.layerName || '').toLowerCase();
        if (
          (dataName.includes('province') && lblName.includes('province')) ||
          (dataName.includes('district') && lblName.includes('district')) ||
          (dataName.includes('neighbor') && lblName.includes('neighbor')) ||
          (dataName.includes('mahalle') && lblName.includes('mahalle')) ||
          lblName.includes(dataName) ||
          dataName.includes(lblName.replace('label', '').trim())
        ) {
          return !!labelVisibility[lbl.id];
        }
      }
      return false;
    },
    [allLayers, labelLayers, labelVisibility],
  );

  const getLayerLegendUrl = useCallback((layer: Layer) => {
    const ws = layer.layerWorkspace?.name || 'cite';
    return geoserverLegendUrl(ws, layer.layerName);
  }, []);

  const isFavorite = useCallback(
    (layerId: number) => !!favorites[layerId],
    [favorites],
  );

  return {
    layers: allLayers,
    borders,
    thematics,
    userOverlays,
    pois,
    labelLayers,
    confLayers,
    thematicGroups,
    poiGroups,
    visibility,
    labelVisibility,
    favorites,
    isLoading,
    error,
    toggleLayerVisibility: handleToggleVisibility,
    toggleLabelVisibility: handleToggleLabelVisibility,
    hasLabel,
    isLabelVisible,
    getLayerLegendUrl,
    isFavorite,
    toggleFavorite,
    clearError,
  };
};
